import { AxiosRequestConfig } from 'axios'
import { AuthStorageInstance } from 'services/Storage/AuthStorage'
import { Mixpanel } from 'services/Tracking/Mixpanel'
import { AuthLocalStoragerEnum } from 'experiments/StaffLink/StaffLink.constants'
import { getBusinessId, isWebView } from 'utils/android'
import { getAndroidToken, waitForInjection } from 'services/Api/apiHelper'
import { LinkManager } from '../../utils'
import { handleUnauthorizedRedirect, isUnauthorizedRedirectInProgress } from './authRedirectHandler'
import { refreshTokenWithMutex } from './tokenRefreshManager'

declare global {
  interface Window {
    deviceIp: string
    isWebView: string
  }
}

export const setNewTokensToLocalStorage = (
  accessToken: string,
  expiry: number,
  refreshToken: string
) => {
  const currentTime = new Date()
  const expiresIn = currentTime.getTime() + (expiry - 60) * 1000
  AuthStorageInstance.setAccessToken(accessToken)
  AuthStorageInstance.setExpiresIn(expiresIn)
  AuthStorageInstance.setRefreshToken(refreshToken)
}

export const apiRequestInterceptor = async (req: AxiosRequestConfig) => {
  try {
    console.log(
      '%c********* Intercepting Request:Success ***********',
      'font-weight:bold;color:white;background-color:black;'
    )
    const token = AuthStorageInstance.getAccessToken()
    if (token) {
      const tokenExpiry = AuthStorageInstance.getExpiresIn()
      const isTokenExpired = tokenExpiry ? tokenExpiry < new Date().getTime() : true
      if (isTokenExpired) {
        console.log(
          '%c********* Token Is Expired ***********',
          'font-weight:bold;color:tomato;background-color:white;'
        )
        try {
          // Mutex ensures only one refresh call happens even if
          // multiple parallel requests detect the expired token.
          const newAccessToken = await refreshTokenWithMutex()
          req.headers.Authorization = `Bearer ${newAccessToken}`
          req.headers['content-type'] = 'application/json'
        } catch (refreshError) {
          // refreshTokenWithMutex already calls handleUnauthorizedRedirect on failure
          return Promise.reject(refreshError)
        }
      } else {
        console.log(
          '%c********* Token Not Expired ***********',
          'font-weight:bold;color:green;background-color:white;'
        )
        req.headers.Authorization = token ? `Bearer ${token}` : ''
      }
    } else {
      handleUnauthorizedRedirect()
      return Promise.reject(new Error('No access token'))
    }
  } catch (err) {
    console.log('Something went wrong with req interceptor: ', err)
    handleUnauthorizedRedirect()
    return Promise.reject(err)
  }
  return req
}

export const apiRequestErrorInterceptor = async (_: any) => {
  console.log(
    '%c********* Intercepting Request:Error  ***********',
    'font-weight:bold;color:black;background-color:red;'
  )
}

export const apiResponseSuccessInterceptor = async (_: any) => {
  console.log(
    '%c********* Intercepting Response:Success ***********',
    'font-weight:bold;color:white;background-color:green;'
  )
  return _
}

export const apiResponseErrorInterceptor = async (error: any) => {
  console.log(
    '%c********* Intercepting Response:Error ***********',
    'font-weight:bold;color:black;background-color:pink;'
  )
  if (error?.response?.status === 401) {
    console.log(
      '%c********* Intercepting 401 Response ***********',
      'font-weight:bold;color:black;background-color:red;'
    )
    handleUnauthorizedRedirect()
    // Return a never-resolving promise to prevent error from bubbling up to
    // components, which would cause them to crash and show a blank screen.
    // The user will be redirected to the login page by handleUnauthorizedRedirect.
    return new Promise(() => {})
  }
  return Promise.reject(error)
}

export const businessIdInterceptor = async (req: any) => {
  try {
    console.log(
      '%c********* Intercepting Request (Business Id):Success ***********',
      'font-weight:bold;color:white;background-color:black;'
    )
    const businessId = AuthStorageInstance.getCurrentBusinessId()
    if (businessId && !req.headers['OKC-Business-Id']) {
      req.headers['OKC-Business-Id'] = businessId
    }
  } catch (err) {
    console.log('Something went wrong with business-id req interceptor: ', err)
  }
  return req
}

export const staffBusinessIdInterceptor = async (req: any) => {
  try {
    console.log(
      '%c********* Intercepting Request (Business Id):Success ***********',
      'font-weight:bold;color:white;background-color:black;'
    )
    const staffBusinessId = localStorage?.getItem(AuthLocalStoragerEnum.staffLinkBusinessId)
    if (staffBusinessId && !req.headers['OKC-Business-Id']) {
      req.headers['OKC-Business-Id'] = staffBusinessId
    }
  } catch (err) {
    console.log('Something went wrong with business-id req interceptor: ', err)
  }
  return req
}
